import { useCallback, useEffect } from 'react';
import type { Editor } from '@tiptap/react';

import { useConfirmImageReplace } from '../../../hook/useConfirmImageReplace';
import { receivePastedFiles } from '../../../util/imageUpload/receivePastedFiles';

export const usePasteFile = ({
  editor,
  shouldConfirm,
  onPaste,
}: {
  editor: Editor | null;
  shouldConfirm: boolean;
  onPaste: (file: File) => void;
}) => {
  const { confirm } = useConfirmImageReplace();

  const handlePaste = useCallback(
    async (event: ClipboardEvent) => {
      const files = receivePastedFiles(event);
      if (!files || files.length === 0) return;

      if (!shouldConfirm) return onPaste(files[0]);

      const shouldReplaceImage = await confirm();

      if (!shouldReplaceImage) return;

      onPaste(files[0]);
    },
    [confirm, onPaste, shouldConfirm],
  );

  useEffect(() => {
    if (!editor) return;
    const handlePasteEvent = ({ event }: { event: ClipboardEvent }) => {
      void handlePaste(event);
    };

    editor.on('paste', handlePasteEvent);
    return () => {
      editor.off('paste', handlePasteEvent);
    };
  }, [editor, handlePaste]);
};
